import { failPendingEntityShareRequests } from "../features/chat/entityShareRequests";
import {
  getOrStartRefresh,
  isAuthInvalidated,
} from "../auth/refreshCoordinator";
import { isRefreshSupersededError } from "../auth/refreshErrors";
import {
  PRESENCE_HEARTBEAT_INTERVAL_MS,
  PRESENCE_SUBSCRIBE_MAX_IDS,
} from "../features/presence/model/presenceConstants";
import {
  clearAllPresenceLeaseTimers,
} from "../features/presence/lib/presenceLeaseTimers";
import { handleEvent } from "./wsEvent";
import { ensureValidToken } from "./wsHelper";
import { buildWsUrl } from "./wsUrl";

/** Observable connect/recovery outcomes for callers and focused tests. */
export const WS_CONNECT_STATUS = {
  OPENED: "opened",
  JOINED: "joined",
  ALREADY_OPEN: "already_open",
  NO_TOKEN: "no_token",
  SUPERSEDED: "superseded",
  FAILED: "failed",
  AUTH_INVALIDATED: "auth_invalidated",
};

let socket = null;
let isConnecting = false;
let manuallyClosed = false;
let reconnectTimer = null;
let presenceHeartbeatTimer = null;
let pendingMessages = [];
let connectInFlight = null;
let connectGeneration = 0;
/** At most one 4401 refresh-and-reconnect per manager generation. */
let authCloseRecoveryConsumed = false;
/** In-flight 4401 recovery promise (testable; null when idle). */
let authRecoveryInFlight = null;
/**
 * Desired presence watch set owned by the manager (survives socket reconnect).
 * Replacement semantics — never chained through generic pendingMessages history.
 */
let desiredPresenceUserIds = [];

function normalizePresenceSubscribeIds(userIds) {
  if (!Array.isArray(userIds)) return [];
  const out = [];
  const seen = new Set();
  for (const raw of userIds) {
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isSafeInteger(n) || n <= 0) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
    if (out.length >= PRESENCE_SUBSCRIBE_MAX_IDS) break;
  }
  return out;
}

function presenceIdsEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function stripQueuedPresenceSubscribe() {
  if (!pendingMessages.length) return;
  pendingMessages = pendingMessages.filter(
    (payload) => !payload || payload.event !== "presence_subscribe",
  );
}

function sendPresenceSubscribePayload(userIds, activeSocket = socket) {
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
    return false;
  }
  try {
    activeSocket.send(
      JSON.stringify({
        event: "presence_subscribe",
        user_ids: userIds.slice(),
      }),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Replay the current desired presence subscription on every authenticated open.
 * Empty desired set is sent so reconnect never restores a cleaned watch list via
 * stale queued payloads (server also starts empty; [] is idempotent).
 */
function flushDesiredPresenceSubscription(activeSocket) {
  sendPresenceSubscribePayload(desiredPresenceUserIds, activeSocket);
}

function setDesiredPresenceSubscription(userIds) {
  const next = normalizePresenceSubscribeIds(userIds);
  const unchanged = presenceIdsEqual(desiredPresenceUserIds, next);
  desiredPresenceUserIds = next;
  // Replacement semantics: drop any historical subscribe payloads from the
  // generic reconnect queue so only the latest desired set is ever sent.
  stripQueuedPresenceSubscribe();

  if (!socket || socket.readyState !== WebSocket.OPEN) {
    return !unchanged;
  }
  if (unchanged) {
    return false;
  }
  sendPresenceSubscribePayload(desiredPresenceUserIds);
  return true;
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function clearPresenceHeartbeatTimer() {
  if (presenceHeartbeatTimer) {
    clearInterval(presenceHeartbeatTimer);
    presenceHeartbeatTimer = null;
  }
}

function stopPresenceHeartbeat() {
  clearPresenceHeartbeatTimer();
}

function startPresenceHeartbeat(activeSocket, generation) {
  // One timer per socket lifecycle — never stack across reconnect joins.
  clearPresenceHeartbeatTimer();
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
    return;
  }
  if (!isGenerationCurrent(generation) || socket !== activeSocket) {
    return;
  }

  const tick = () => {
    if (!isGenerationCurrent(generation) || socket !== activeSocket) {
      clearPresenceHeartbeatTimer();
      return;
    }
    if (activeSocket.readyState !== WebSocket.OPEN) {
      return;
    }
    try {
      activeSocket.send(JSON.stringify({ event: "presence_heartbeat" }));
    } catch {
      // Fail soft — transport errors surface via onclose.
    }
  };

  // Immediate refresh on open, then bounded cadence (not sub-10s).
  tick();
  presenceHeartbeatTimer = setInterval(tick, PRESENCE_HEARTBEAT_INTERVAL_MS);
}

function isGenerationCurrent(generation) {
  return generation === connectGeneration && !manuallyClosed;
}

function endConnecting(generation) {
  if (generation === connectGeneration) {
    isConnecting = false;
  }
}

function attachSocketHandlers(activeSocket, dispatch, getState, generation) {
  activeSocket.onopen = () => {
    if (!isGenerationCurrent(generation) || socket !== activeSocket) {
      try {
        activeSocket.close();
      } catch {
        // ignore close races
      }
      return;
    }

    endConnecting(generation);

    // Ordinary chat/entity-share traffic may queue; presence_subscribe must not
    // accumulate obsolete replacement payloads across reconnects.
    stripQueuedPresenceSubscribe();
    pendingMessages.forEach((payload) => {
      activeSocket.send(JSON.stringify(payload));
    });

    pendingMessages = [];
    flushDesiredPresenceSubscription(activeSocket);
    startPresenceHeartbeat(activeSocket, generation);
  };

  activeSocket.onmessage = (e) => {
    if (!isGenerationCurrent(generation) || socket !== activeSocket) {
      return;
    }

    let data;
    try {
      data = JSON.parse(e.data);
    } catch {
      return;
    }

    if (data.event === "batch") {
      if (!Array.isArray(data.payload)) return;
      data.payload.forEach((item) =>
        handleEvent(item, dispatch, getState, wsManager.send),
      );
      return;
    }

    handleEvent(data, dispatch, getState, wsManager.send);
  };

  activeSocket.onclose = (event) => {
    if (socket === activeSocket) {
      socket = null;
    }
    endConnecting(generation);
    stopPresenceHeartbeat();

    // Entity-share send uses no-queue delivery; pending acks must not hang.
    failPendingEntityShareRequests();

    if (!isGenerationCurrent(generation)) {
      return;
    }

    if (event.code === 4403) {
      // Authorization/forbidden — fail closed; no refresh or reconnect.
      manuallyClosed = true;
      clearReconnectTimer();
      stopPresenceHeartbeat();
      return;
    }

    if (event.code === 4401) {
      stopPresenceHeartbeat();
      const recoveryPromise = recoverFromAuthClose(
        dispatch,
        getState,
        generation,
      );
      authRecoveryInFlight = recoveryPromise;
      void recoveryPromise.finally(() => {
        if (authRecoveryInFlight === recoveryPromise) {
          authRecoveryInFlight = null;
        }
      });
      return;
    }

    if (manuallyClosed) return;

    scheduleGenericReconnect(dispatch, getState);
  };

  activeSocket.onerror = () => {
    // Keep diagnostic noise token-free; transport errors surface via onclose.
  };
}

function failClosedCurrentGeneration(generation) {
  // Only the owning generation may mutate global lifecycle flags/timers.
  if (generation !== connectGeneration) {
    return;
  }
  manuallyClosed = true;
  clearReconnectTimer();
}

function isUsableConnectRecoveryResult(result) {
  if (!result || !result.status) return false;
  if (result.status === WS_CONNECT_STATUS.OPENED) return true;
  if (result.status === WS_CONNECT_STATUS.ALREADY_OPEN) return true;
  if (result.status === WS_CONNECT_STATUS.JOINED) {
    return isUsableConnectRecoveryResult(result.shared);
  }
  return false;
}

async function recoverFromAuthClose(dispatch, getState, generation) {
  // Stale generation must never mutate the replacement/current lifecycle.
  if (generation !== connectGeneration) {
    return;
  }

  if (authCloseRecoveryConsumed || isAuthInvalidated() || manuallyClosed) {
    failClosedCurrentGeneration(generation);
    return;
  }

  const storedRefresh = getState()?.userLogin?.userInfo?.refresh;
  if (!storedRefresh) {
    failClosedCurrentGeneration(generation);
    return;
  }

  authCloseRecoveryConsumed = true;
  clearReconnectTimer();

  try {
    await getOrStartRefresh(dispatch);

    if (generation !== connectGeneration) {
      return;
    }

    if (isAuthInvalidated() || manuallyClosed) {
      failClosedCurrentGeneration(generation);
      return;
    }

    const result = await wsManager.connect(dispatch, getState);

    if (generation !== connectGeneration) {
      return;
    }

    if (!isUsableConnectRecoveryResult(result)) {
      // FAILED / AUTH_INVALIDATED / SUPERSEDED / NO_TOKEN are not recovery.
      failClosedCurrentGeneration(generation);
    }
  } catch (error) {
    // Superseded stale work and generation replacement must not poison the
    // current manager lifecycle. Terminal failure for the unchanged current
    // generation remains fail-closed (coordinator owns logout/clear/redirect).
    if (generation !== connectGeneration) {
      return;
    }
    if (isRefreshSupersededError(error)) {
      return;
    }
    failClosedCurrentGeneration(generation);
  }
}

function scheduleGenericReconnect(dispatch, getState) {
  if (manuallyClosed || isAuthInvalidated()) {
    return;
  }

  // Deduplicate: at most one reconnect timer for the manager generation.
  if (reconnectTimer) {
    return;
  }

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (manuallyClosed || isAuthInvalidated()) {
      return;
    }
    void wsManager.connect(dispatch, getState);
  }, 2000);
}

async function runConnectAttempt(dispatch, getState, generation) {
  try {
    let token = getState().userLogin.userInfo?.access;
    if (!token) {
      endConnecting(generation);
      return { status: WS_CONNECT_STATUS.NO_TOKEN };
    }

    token = await ensureValidToken(dispatch, getState, token);

    if (!isGenerationCurrent(generation)) {
      endConnecting(generation);
      return { status: WS_CONNECT_STATUS.SUPERSEDED };
    }

    if (isAuthInvalidated()) {
      endConnecting(generation);
      return { status: WS_CONNECT_STATUS.AUTH_INVALIDATED };
    }

    if (!token) {
      endConnecting(generation);
      return { status: WS_CONNECT_STATUS.NO_TOKEN };
    }

    // A concurrent join may have observed the same attempt; still open once.
    if (socket) {
      endConnecting(generation);
      return { status: WS_CONNECT_STATUS.ALREADY_OPEN };
    }

    const activeSocket = new WebSocket(buildWsUrl(token));
    socket = activeSocket;
    attachSocketHandlers(activeSocket, dispatch, getState, generation);

    return { status: WS_CONNECT_STATUS.OPENED };
  } catch (error) {
    endConnecting(generation);
    if (!isGenerationCurrent(generation)) {
      return { status: WS_CONNECT_STATUS.SUPERSEDED };
    }
    if (isAuthInvalidated()) {
      return { status: WS_CONNECT_STATUS.AUTH_INVALIDATED, error };
    }
    return { status: WS_CONNECT_STATUS.FAILED, error };
  }
}

export const wsManager = {
  connect: async (dispatch, getState) => {
    if (socket) {
      if (connectInFlight) {
        const shared = await connectInFlight;
        return { status: WS_CONNECT_STATUS.JOINED, shared };
      }
      return { status: WS_CONNECT_STATUS.ALREADY_OPEN };
    }

    if (connectInFlight) {
      const shared = await connectInFlight;
      return { status: WS_CONNECT_STATUS.JOINED, shared };
    }

    if (isAuthInvalidated()) {
      return { status: WS_CONNECT_STATUS.AUTH_INVALIDATED };
    }

    manuallyClosed = false;
    clearReconnectTimer();

    const generation = connectGeneration;
    isConnecting = true;

    const attempt = runConnectAttempt(dispatch, getState, generation);
    connectInFlight = attempt;

    try {
      return await attempt;
    } finally {
      if (connectInFlight === attempt) {
        connectInFlight = null;
      }
    }
  },

  /**
   * @param {object} payload
   * @param {{ queue?: boolean }} [options]
   *   queue defaults to true (generic chat reconnect buffer).
   *   Pass queue:false for entity-share so disconnected sends fail immediately
   *   instead of silently entering the unbounded reconnect queue.
   */
  send: (payload, options = {}) => {
    // Presence subscribe is owned by the desired-subscription coordinator —
    // never enter the generic pendingMessages chain (replacement semantics).
    if (payload && payload.event === "presence_subscribe") {
      return setDesiredPresenceSubscription(payload.user_ids);
    }

    const shouldQueue = options.queue !== false;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      if (shouldQueue) {
        pendingMessages.push(payload);
      }
      return false;
    }

    socket.send(JSON.stringify(payload));
    return true;
  },

  /**
   * Own the latest desired presence watch set independently of socket instance.
   * Replays on every authenticated open; empty clears obsolete watchers.
   */
  setDesiredPresenceSubscription,

  getDesiredPresenceSubscriptionForTests: () => desiredPresenceUserIds.slice(),

  /** Test/helper: expose reconnect queue length without mutating lifecycle. */
  getPendingQueueLengthForTests: () => pendingMessages.length,

  clearPendingQueueForTests: () => {
    pendingMessages = [];
  },

  /**
   * Deterministic lifecycle snapshot for focused tests.
   * Does not expose tokens, URLs, or credential-bearing fields.
   */
  getLifecycleStateForTests: () => ({
    hasSocket: !!socket,
    socketReadyState: socket ? socket.readyState : null,
    isConnecting,
    manuallyClosed,
    hasReconnectTimer: !!reconnectTimer,
    hasPresenceHeartbeatTimer: !!presenceHeartbeatTimer,
    connectGeneration,
    authCloseRecoveryConsumed,
    hasConnectInFlight: !!connectInFlight,
    hasAuthRecoveryInFlight: !!authRecoveryInFlight,
    pendingQueueLength: pendingMessages.length,
    desiredPresenceUserIds: desiredPresenceUserIds.slice(),
  }),

  /** Awaitable 4401 recovery episode for focused tests. */
  getAuthRecoveryInFlightForTests: () => authRecoveryInFlight,

  disconnect: () => {
    connectGeneration += 1;
    manuallyClosed = true;
    isConnecting = false;
    connectInFlight = null;
    authCloseRecoveryConsumed = false;
    pendingMessages = [];
    desiredPresenceUserIds = [];

    clearReconnectTimer();
    stopPresenceHeartbeat();
    clearAllPresenceLeaseTimers();

    if (socket) {
      const closing = socket;
      socket = null;
      try {
        closing.close();
      } catch {
        // ignore
      }
    }
  },
};

/** Full module reset for Jest isolation (does not navigate or clear auth). */
export function __resetWsManagerForTests() {
  clearReconnectTimer();
  stopPresenceHeartbeat();
  clearAllPresenceLeaseTimers();
  if (socket) {
    try {
      socket.close();
    } catch {
      // ignore
    }
  }
  socket = null;
  isConnecting = false;
  manuallyClosed = false;
  pendingMessages = [];
  desiredPresenceUserIds = [];
  connectInFlight = null;
  connectGeneration = 0;
  authCloseRecoveryConsumed = false;
  authRecoveryInFlight = null;
}
